import {ResolutionDate} from "./ResolutionDate";
import {ResolutionDateTime} from "./ResolutionDateTime";
import {ResolutionTime} from "./ResolutionTime";
import {ResolutionToday} from "./ResolutionToday";
import type SliderData from "./SliderData";
import {formatInTimeZone} from "date-fns-tz";

export type ResolutionType = 'number' | 'date' | 'time' | 'datetime' | 'today';

export class ResolutionConverter {

    private resolution: ResolutionType;
    private resolutionValue: number;
    private allowToExtendRange: boolean;

    constructor(resolution: ResolutionType, resolutionValue: number = 1, allowToExtendRange: boolean = false) {
        this.resolution = resolution;
        this.resolutionValue = resolutionValue;
        this.allowToExtendRange = allowToExtendRange;
    }

    public canExtendRange(): boolean {
        return this.allowToExtendRange;
    }

    public getFormatted(value:number, useShortFormat: boolean = false): string {
        value = Math.floor(value * this.resolutionValue);
        switch (this.resolution) {
            case 'date':
                return ResolutionDate.getDateStringFromDays(Math.floor(value), useShortFormat);
            case 'time':
                return ResolutionTime.getDateStringFromMinutes(Math.floor(value));
            case 'datetime':
                return ResolutionDateTime.getDateStringFromMinutes(Math.floor(value));
            case 'today':
                return ResolutionToday.getTodayStringFromDays(Math.floor(value));
            default:
                return value.toString();
        }
    }

    public getNumber(strValue: string): number {
        switch (this.resolution) {
            case 'date': {
                if (this.isValidDateString(strValue)) {
                    return ResolutionDate.getDaysFromDate(new Date(strValue)) / this.resolutionValue;
                } else {
                    throw new Error('Invalid date string: ' + strValue);
                }
            }
            case 'time': {
                let time = '1970-01-01 ' + strValue;
                if (this.isValidDateString(time)) {
                    // console.warn('ResolutionConvert time', time, ResolutionTime.getMinutesFromDate(new Date(time)));
                    return ResolutionTime.getMinutesFromDate(new Date(time)) / this.resolutionValue;
                } else {
                    throw new Error('Invalid time string: ' + strValue);
                }
            }
            case 'datetime': {
                if (this.isValidDateString(strValue)) {
                    return ResolutionDateTime.getMinutesFromDate(new Date(strValue)) / this.resolutionValue;
                } else {
                    throw new Error('Invalid datetime string: ' + strValue);
                }
            }
            case 'today': {
                if (ResolutionToday.isValidTodayString(strValue)) {
                    return ResolutionToday.getDaysFromTodayString(strValue) / this.resolutionValue;
                } else {
                    throw new Error('Invalid datetime string: ' + strValue);
                }
            }
            default:
                return Number(strValue) / this.resolutionValue;
        }
    }

    public isValid(str: string): boolean {
        try {
            this.getNumber(str);
            return true;
        } catch (e) {
            console.warn(e);
            return false;
        }
    }

    private isValidDateString(value: string): boolean {
        return !isNaN(Date.parse(value));
    }

    public setResolution(resolution: ResolutionType, resolutionValue: number = 1): this {
        this.resolution = resolution;
        this.resolutionValue = resolutionValue;
        return this;
    }

    public getResolution(): ResolutionType {
        return this.resolution;
    }

    public getResolutionValue(): number {
        return this.resolutionValue;
    }

    public addCalendarPlugin(
        root: HTMLDivElement,
        sliderData: SliderData,
        blockId: number,
        onChange: (day_string: string, value: number) => void
    ) {
        let day = '';
        // console.log('addCalendarPlugin', sliderData);
        let settings = {
            // alwaysShowCalendars: false,
            showCustomRangeLabel: false,
            singleDatePicker: true,
            autoApply: true,
            drops: 'up',
            parentElement: root,
            // dateContainer: root,
            // minYear: 2023,
            // maxYear: parseInt(m().format('YYYY'), 10),
            startDate: this.getFormatted(sliderData.getBlock(blockId).max),
            // endDate: day,
            // minDate: this.getFormatted(sliderData.getVisibleMin()),
            // maxDate: this.getFormatted(sliderData.getVisibleMax()),
            locale: {
                format: 'YYYY-MM-DD',
                cancelLabel: "Anuluj",
                applyLabel: "Wybierz",
                "daysOfWeek": [
                    "Nd",
                    "Pn",
                    "Wt",
                    "Śr",
                    "Cz",
                    "Pt",
                    "So"
                ],
                "monthNames": [
                    "Styczeń",
                    "Luty",
                    "Marzec",
                    "Kwiecień",
                    "Maj",
                    "Czerwiec",
                    "Lipiec",
                    "Sierpień",
                    "Wrzesień",
                    "Październik",
                    "Listopad",
                    "Grudzień"
                ],
                "firstDay": 1
            },
            applyClass: 'date-range-button-apply',
            cancelClass: 'date-range-button-cancel',
        };

        if (!this.allowToExtendRange) {
            settings = {
                ...settings,
                minDate: this.getFormatted(sliderData.getVisibleMin()),
                maxDate: this.getFormatted(sliderData.getVisibleMax()),
            }
        }

        if (true) { // dont allow to set max date before min date
            if (blockId===0) {
                settings = {
                    ...settings,
                    maxDate: this.getFormatted(sliderData.getBlock(blockId+1).max),
                }
            }
            if (blockId===1) {
                settings = {
                    ...settings,
                    minDate: this.getFormatted(sliderData.getBlock(blockId-1).max),
                }
            }
        }

        // check if minDate and maxData has the same year and if yes change locale.format to 'MM-DD'
        // if (settings.minDate && settings.maxDate) {
        //     let minDate = new Date(settings.minDate);
        //     let maxDate = new Date(settings.maxDate);
        //     if (minDate.getFullYear() === maxDate.getFullYear()) {
        //         settings.locale.format = 'DD-MM';
        //     }
        // }

        jQuery(root).daterangepicker(settings,  (start, end, label)=> {
            let day_string = start.format('YYYY-MM-DD');
            let value_number = this.getNumber(day_string);
            if (typeof onChange === 'function') onChange(day_string, value_number);
        });

        // @ts-ignore jQuery is not recognized
        jQuery('.daterangepicker').css('z-index', 20000000).css('box-shadow','-1px 11px 31px -10px rgba(0, 0, 0, 0.75)');
    }


}